import moment, { Moment } from 'moment';
import { formatBuddhistEra } from 'src/app/shared/pipes/thai-datetime.pipe';
import {
    map as _map,
    toNumber as _toNumber,
    toString as _toString,
    isFinite as _isFinite,
} from 'lodash';
import { bahttext } from 'bahttext';
import {
    Item,
    TaxInvoicePrintSheetParameter,
} from 'src/app/shared/services/print-sheet/interfaces/tax-invoice-param';
import {
    OrderItem,
    OutputTax,
} from 'src/app/shared/services/output-tax/interfaces/output-tax-request';
import {
    SellerEntity,
    toSellerPrintBlock,
} from 'src/app/shared/config/entity-config';
import { ProductInfo, TaxInvoiceInput } from './invoice-input';

// The builders that turn the tax-invoice form state into the two wire shapes:
// the output-tax save payload (flat dotted keys — the Lambda $sets them as
// nested paths) and the jsreport print-sheet parameter.
//
// Extracted from create-tax-invoice.component.ts as PURE functions taking the
// seller as a parameter, so that the seller source can move from the
// module-level `sellerInfo` constant to the entity registry without touching
// the construction logic — and so the fixture specs beside this file pin the
// built objects byte-for-byte across that move. Behaviour must match the
// component code it came from exactly; a difference here is a regression, not
// a cleanup.

// The six fields a document prints for its seller. Field names match the
// template contract (tax-invoice/content.handlebars `sellerInfo.*`).
export interface SellerPrintBlock {
    name: string;
    branch: string;
    taxId: string;
    website: string;
    address: string;
    email: string;
}

export const formatDisplayDate = (
    input: Date | null,
    abb?: boolean,
): string => {
    if (input) {
        return formatBuddhistEra(
            moment(input),
            abb ? 'DD/MM/YYYY' : 'DD MMMM YYYY',
        );
    }
    return '';
};

const formatString = (input: any): string => {
    return _toString(input).trim();
};

// Composes title1/2/3 from the checked document types: the Thai heading, the
// English subtitle, and the ต้นฉบับ/สำเนา corner mark with (เอกสารออกเป็นชุด)
// appended when more than one document type shares the sheet. Free-text
// overrides on the form win over all of it.
export const constructTitle = (
    input: TaxInvoiceInput,
    isCopy: boolean,
): { h1: string; h2: string; h3: string } => {
    let h1 = '';
    let h2 = '';
    let h3 = isCopy ? 'สำเนา' : 'ต้นฉบับ';

    const h1List = [];
    const h2List = [];
    if (
        input.invoiceInfo.isDeliveryNote ||
        input.invoiceInfo.isTaxInvoice ||
        input.invoiceInfo.isReceipt
    ) {
        if (input.invoiceInfo.isDeliveryNote) {
            h1List.push('ใบส่งสินค้า');
            h2List.push('Delivery Note');
        }
        if (input.invoiceInfo.isTaxInvoice) {
            h1List.push('ใบกำกับภาษี');
            h2List.push('Tax Invoice');
        }
        if (input.invoiceInfo.isReceipt) {
            h1List.push('ใบเสร็จรับเงิน');
            h2List.push('Receipt');
        }
        h1 = h1List.join(' / ');
        h2 = `(${h2List.join(' / ')})`;
    }
    if (h1List.length > 1) {
        h3 = `${h3} (เอกสารออกเป็นชุด)`;
    }

    const userTitle1: string = input.invoiceInfo.title1;
    const userTitle2: string = input.invoiceInfo.title2;
    const userTitle3: string = input.invoiceInfo.title3;
    if (userTitle1) {
        h1 = userTitle1;
    }
    if (userTitle2) {
        h2 = userTitle2;
    }
    if (userTitle3) {
        h3 = userTitle3;
    }
    return {
        h1,
        h2,
        h3,
    };
};

// Takes the ENTITY rather than a bare print block so the row's entityId and
// its sellerInfo snapshot cannot disagree by construction — guard G2 then
// only has to catch rows built by hand elsewhere.
export const buildInvoiceSaveRequest = (
    input: TaxInvoiceInput,
    entity: SellerEntity,
): OutputTax => {
    const seller = toSellerPrintBlock(entity);
    const invoiceMoment: Moment = moment(input.invoiceInfo.date);
    const title = constructTitle(input, false);
    const docSet =
        [
            input.invoiceInfo.isDeliveryNote,
            input.invoiceInfo.isTaxInvoice,
            input.invoiceInfo.isReceipt,
        ].filter(Boolean).length > 1;
    const param: OutputTax = {
        fileSharedLink: null,
        entityId: entity.id,
        // The manual form always collects a customer block, so a document
        // issued WITH the tax-invoice switch on is the full ม.86/4 shape;
        // with it off, no tax invoice was issued and the row must say so —
        // the สำเนา rebuild keys its headings off this.
        docType: input.invoiceInfo.isTaxInvoice ? 'full' : 'none',
        // The exact heading/signature arrangement the ต้นฉบับ printed, so
        // the month-end สำเนา reproduces the issued document.
        'docInfo.title1': title.h1,
        'docInfo.title2': title.h2,
        'docInfo.docSet': docSet,
        'docInfo.hideSignatureFields': !input.invoiceInfo.showSignatureFields,
        'docInfo.hideSellerSignature': !input.invoiceInfo.showSellerSignature,
        'docInfo.priceIncludesVat': input.invoiceInfo.priceIncludesVat,
        'sellerInfo.name': seller.name,
        'sellerInfo.branch': seller.branch,
        'sellerInfo.taxId': seller.taxId,
        'sellerInfo.website': seller.website,
        'sellerInfo.address': seller.address,
        'sellerInfo.email': seller.email,
        'customerInfo.name': formatString(input.customerInfo.name),
        'customerInfo.branch': formatString(input.customerInfo.branch),
        'customerInfo.address': formatString(
            input.customerInfo.useCustomAddress
                ? input.customerInfo.customAddress
                : input.customerInfo.fullAddress,
        ),
        'customerInfo.taxId': formatString(input.customerInfo.taxId),
        'orderInfo.invNum': input.invoiceInfo.isEdit
            ? input.invoiceInfo.invNum
            : null,
        'orderInfo.orderNo': _toNumber(input.invoiceInfo.refOrderNum),
        'orderInfo.date': invoiceMoment.isValid()
            ? _toNumber(invoiceMoment.format('YYYYMMDD'))
            : null,
        'orderInfo.dateD': invoiceMoment.isValid()
            ? _toNumber(invoiceMoment.format('D'))
            : null,
        'orderInfo.dateM': invoiceMoment.isValid()
            ? _toNumber(invoiceMoment.format('M'))
            : null,
        'orderInfo.dateY': invoiceMoment.isValid()
            ? _toNumber(invoiceMoment.format('YYYY'))
            : null,
        'orderInfo.totalProductPrice':
            input.invoiceInfo.tableTotal - input.invoiceInfo.shippingCost || 0,
        'orderInfo.discount': input.invoiceInfo.discount || 0,
        'orderInfo.shippingCost': input.invoiceInfo.shippingCost || 0,
        'orderInfo.amtBeforeVat': input.invoiceInfo.amtBeforeVat || 0,
        'orderInfo.totalVat': input.invoiceInfo.vatAmount || 0,
        'orderInfo.netPrice': input.invoiceInfo.netPrice || 0,
        'orderInfo.remark': formatString(input.invoiceInfo.remark),
        items: _map<ProductInfo, OrderItem>(
            input.productItems,
            (item: ProductInfo): OrderItem => {
                return {
                    pid: item.pid,
                    sku: item.sku,
                    itemNo: item.itemNo,
                    description: formatString(item.description),
                    unitPrice: item.unitPrice,
                    quantity: item.quantity,
                    amount: item.amount,
                };
            },
        ),
    };
    return param;
};

export const buildTaxInvoicePrintSheetRequest = (
    input: TaxInvoiceInput,
    seller: SellerPrintBlock,
    invNum: string,
    isCopy = false,
): TaxInvoicePrintSheetParameter => {
    const title = constructTitle(input, isCopy);
    const requestParam: TaxInvoicePrintSheetParameter = {
        sellerInfo: {
            name: seller.name,
            branch: seller.branch,
            taxId: seller.taxId,
            website: seller.website,
            address: seller.address,
            email: seller.email,
        },
        customerInfo: {
            name: formatString(input.customerInfo.name),
            branch: formatString(input.customerInfo.branch),
            address: formatString(
                input.customerInfo.useCustomAddress
                    ? input.customerInfo.customAddress
                    : input.customerInfo.fullAddress,
            ),
            taxId: formatString(input.customerInfo.taxId),
        },
        orderInfo: {
            invNum,
            orderNo: _toString(input.invoiceInfo.refOrderNum),
            date: formatDisplayDate(input.invoiceInfo.date),
            totalExclVat: input.invoiceInfo.amtBeforeVat || 0,
            vatAmt: input.invoiceInfo.vatAmount || 0,
            remark: formatString(input.invoiceInfo.remark),
            tableTotal: input.invoiceInfo.tableTotal || 0,
            discount: input.invoiceInfo.discount || 0,
            shippingCost: input.invoiceInfo.shippingCost || 0,
            netPrice: input.invoiceInfo.netPrice || 0,
            netPriceInWords: _isFinite(input.invoiceInfo.netPrice)
                ? bahttext(input.invoiceInfo.netPrice)
                : '',
            sellerSignDate: formatDisplayDate(input.invoiceInfo.date, true),
        },
        docInfo: {
            hideSellerSignature: !input.invoiceInfo.showSellerSignature,
            hideSignatureFields: !input.invoiceInfo.showSignatureFields,
            title1: title.h1,
            title2: title.h2,
            title3: title.h3,
            priceIncludesVat: input.invoiceInfo.priceIncludesVat,
        },
        items: _map<ProductInfo, Item>(
            input.productItems,
            (item: ProductInfo): Item => {
                return {
                    itemNo: item.itemNo,
                    description: formatString(item.description),
                    unitPrice: item.unitPrice,
                    quantity: item.quantity,
                    amount: item.amount,
                };
            },
        ),
    };
    return requestParam;
};
